import { useState, MouseEvent } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Avatar,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useUserContext, useFilterJobs } from "@/providers/contexts";
import logo from "@/components/logo2.png";
import { useLogoutUser } from "@/hooks";
import { UserAvatar } from "@/components/views/User/Profile/UserAvatar";

const pages = ["Search", "Directory", "Memberships", "Changelog"];
const guestSettings = ["Login", "Register"];
const userSettings = ["Profile", "Account", "Logout"];

function NavBar() {
  const { isAuthenticated, disabledBasicContent, disabledProContent, user } =
    useUserContext();
  const { resetFilters } = useFilterJobs();
  const { logoutUser } = useLogoutUser();

  const disabledContent =
    disabledBasicContent || disabledProContent || !isAuthenticated;

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorMobileEl, setAnchorMobileEl] = useState<null | HTMLElement>(
    null,
  );

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenMobileMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorMobileEl(event.currentTarget);
  };

  const handleCloseMobileMenu = () => {
    setAnchorMobileEl(null);
  };

  const handleNavigation = (page: string) => {
    if (page === "Directory" && disabledContent) {
      window.location.href = "/login";
    } else {
      // Reset filters before navigating to a new page
      resetFilters();
      window.location.href = `/${page.toLowerCase()}`;
    }
  };

  const handleSettingNavigation = async (setting: string) => {
    if (setting === "Logout") {
      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken) {
          await logoutUser(refreshToken);
        }

        // Clear tokens
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");

        window.location.href = "/";
      } catch (error) {
        console.error("Error logging out:", error);
        alert("There was an issue logging out. Please try again.");
      }
    } else {
      window.location.href = `/${setting.toLowerCase()}`;
    }
  };

  const handleHomeNavigation = () => {
    resetFilters();
    window.location.href = "/";
  };

  return (
    <Box
      data-testid="top-navbar"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <AppBar color="transparent" position="static" sx={{ boxShadow: "none" }}>
        <Container>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Button
              onClick={handleHomeNavigation}
              sx={{
                textTransform: "none",
                padding: 0,
                textAlign: "left",
              }}
            >
              <img
                src={logo}
                alt="PharmDs in IT Logo"
                style={{
                  width: 50,
                  height: 50,
                  marginRight: 10,
                  borderRadius: "4px",
                }}
              />
              <Typography variant="title" component="div" sx={{ fontSize: 24 }}>
                PharmDs in IT
              </Typography>
            </Button>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
              }}
            >
              {pages.map(page => (
                <Tooltip
                  key={page}
                  title={
                    page === "Directory" && disabledContent
                      ? "Upgrade your account to access the directory of companies"
                      : ""
                  }
                >
                  <span>
                    <Button
                      onClick={() => handleNavigation(page)}
                      sx={{
                        m: 2,
                        color: "black",
                        display: "block",
                        letterSpacing: "0.05em",
                      }}
                      disabled={page === "Directory" && disabledContent}
                    >
                      {page}
                    </Button>
                  </span>
                </Tooltip>
              ))}
            </Box>

            {/* Mobile Menu Icon */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleOpenMobileMenu}
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <Tooltip title="Open navigation menu">
                <MenuIcon />
              </Tooltip>
            </IconButton>

            {/* Mobile Navigation Menu */}
            <Menu
              sx={{ mt: "45px" }}
              anchorEl={anchorMobileEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorMobileEl)}
              onClose={handleCloseMobileMenu}
            >
              {pages.map(page => (
                <Tooltip
                  key={page}
                  title={
                    page === "Directory" && disabledContent
                      ? "Upgrade your account to access the directory of companies"
                      : ""
                  }
                >
                  <span>
                    <MenuItem
                      onClick={() => {
                        handleCloseMobileMenu();
                        handleNavigation(page);
                      }}
                      disabled={page === "Directory" && disabledContent}
                      sx={{
                        cursor:
                          page === "Directory" && disabledContent
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          page === "Directory" && disabledContent ? 0.5 : 1,
                      }}
                    >
                      {page}
                    </MenuItem>
                  </span>
                </Tooltip>
              ))}
              <Divider />
              {(isAuthenticated ? userSettings : guestSettings).map(setting => (
                <MenuItem
                  data-testid="menu-icon"
                  key={setting}
                  onClick={() => {
                    handleCloseMobileMenu();
                    handleSettingNavigation(setting);
                  }}
                >
                  {setting}
                </MenuItem>
              ))}
            </Menu>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Tooltip title="Open settings">
                <IconButton
                  data-testid="menu-icon"
                  onClick={handleOpenUserMenu}
                  sx={{ p: 0 }}
                >
                  {user ? (
                    <UserAvatar
                      width={40}
                      height={40}
                      email={user?.email}
                      firstName={user?.first_name}
                      lastName={user?.last_name}
                    />
                  ) : (
                    <Avatar alt="User Avatar" />
                  )}
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {(isAuthenticated ? userSettings : guestSettings).map(
                  setting => (
                    <MenuItem
                      key={setting}
                      onClick={() => {
                        handleCloseUserMenu();
                        handleSettingNavigation(setting);
                      }}
                    >
                      {setting}
                    </MenuItem>
                  ),
                )}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        sx={{
          width: "80%",
          borderBottom: "1px solid #D3D3D3",
          margin: "0 auto",
          pt: 1,
        }}
      />
    </Box>
  );
}
export default NavBar;
